import { useState, useEffect, useRef } from 'react';
import { fetchEventSource } from '@microsoft/fetch-event-source';

const useGlobalCallStream = (businessId, token) => {
    const [liveTranscripts, setLiveTranscripts] = useState({});
    const abortControllerRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const retryCountRef = useRef(0);

    useEffect(() => {
        if (!businessId || !token) return;

        const connect = async () => {
            // Strict Cleanup: Ensure to close existing connection before creating a new one
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }

            const baseUrl = process.env.REACT_APP_API_URL || '';
            // Cache bypass: We pass the auth token as a query parameter and add timestamp `t` to bypass the service worker and avoid cache
            const url = `${baseUrl}/business/${businessId}/stream?auth_token=${encodeURIComponent(token)}&t=${Date.now()}`;

            const abortController = new AbortController();
            abortControllerRef.current = abortController;

            try {
                await fetchEventSource(url, {
                    method: 'GET',
                    headers: {
                        'x-business-id': businessId.toString()
                    },
                    signal: abortController.signal,
                    onopen(response) {
                        if (response.ok) {
                            // Connection established, reset the backoff retry count
                            retryCountRef.current = 0;
                            return; // everything's good
                        } else {
                            throw new Error(`SSE connection failed with status: ${response.status}`);
                        }
                    },
                    onmessage(event) {
                        try {
                            // Empty ping comments or other heartbeats usually don't trigger onmessage or don't parse
                            const data = JSON.parse(event.data);

                            if (data && data.mapping_id) {
                                setLiveTranscripts(prev => {
                                    const prevTranscripts = prev[data.mapping_id] || [];
                                    const messageExists = prevTranscripts.some(t => t.id === data.id);

                                    if (messageExists) {
                                        return prev;
                                    }

                                    return {
                                        ...prev,
                                        [data.mapping_id]: [...prevTranscripts, data]
                                    };
                                });
                            }
                        } catch (e) {
                            console.error("Failed to parse SSE event data:", e);
                        }
                    },
                    onclose() {
                        throw new Error("SSE connection closed"); // throw to trigger reconnect
                    },
                    onerror(error) {
                        console.error("SSE connection error:", error);
                        throw error; // throw to trigger outer catch for exponential backoff
                    }
                });
            } catch (error) {
                if (abortController.signal.aborted) {
                    return; // Normal cleanup on unmount
                }

                // Exponential backoff starting at 5s, capped at max 60s
                const backoffDelay = Math.min(5000 * Math.pow(2, retryCountRef.current), 60000);
                retryCountRef.current += 1;

                console.log(`Reconnecting to SSE stream in ${backoffDelay}ms... (Attempt ${retryCountRef.current})`);
                reconnectTimeoutRef.current = setTimeout(() => {
                    connect();
                }, backoffDelay);
            }
        };

        connect();

        // Standard cleanup on unmount or deps change
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
                abortControllerRef.current = null;
            }
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };
    }, [businessId, token]);

    return liveTranscripts;
};

export default useGlobalCallStream;
